import { useState } from 'react';
import { App } from 'antd';
import { useNavigate } from 'react-router-dom';
import { getPassgateLoginUrl, loginByPassword } from '@apis/auth';
import { PASSGATE_RETURN_PATH_KEY } from '@/common/auth/passgateTokens';
import { setLoginTokens } from '@/common/auth/tokens';
import { resetCurrentUserCache } from '@/common/auth/adminAcl';
import './index.less';

type LoginMode = 'feishu' | 'password';

const NOW_LABEL = new Date().toISOString().slice(0, 16).replace('T', ' ');

/**
 * Feishu/Lark 品牌 mark（简化几何版）。
 *
 * 形态：teal 渐变圆角方块（对齐 Lark app icon 主色 #00D6B9）+ 内嵌白色聊天
 * 气泡 + 三个 teal 圆点（"对话中"指示）。所有路径都是规整圆角矩形 / 圆，
 * 22 ~ 28px 缩放都不畸变。
 */
const FeishuIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 32 32"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <defs>
      <linearGradient id="ul-feishu-bg" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#33E0C2" />
        <stop offset="100%" stopColor="#00B5A4" />
      </linearGradient>
    </defs>
    <rect width="32" height="32" rx="7.5" fill="url(#ul-feishu-bg)" />
    {/* 聊天气泡：圆角矩形 + 右下尖角 */}
    <path
      d="M10 9h12a2.2 2.2 0 0 1 2.2 2.2v7.6A2.2 2.2 0 0 1 22 21h-6.4l-4.1 3.2a0.4 0.4 0 0 1-0.65-0.31V21H10a2.2 2.2 0 0 1-2.2-2.2v-7.6A2.2 2.2 0 0 1 10 9z"
      fill="#fff"
    />
    {/* 对话三点 */}
    <circle cx="13" cy="15" r="1.2" fill="#00B5A4" />
    <circle cx="16" cy="15" r="1.2" fill="#00B5A4" />
    <circle cx="19" cy="15" r="1.2" fill="#00B5A4" />
  </svg>
);

const InfoIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.2" />
    <path d="M8 7v4.5M8 4.6h.01" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

/**
 * 仿真平台登录页。
 *
 * 设计意图：mission-control / blueprint —— 左侧蓝图工程视觉区，右侧紧凑玻璃卡。
 *
 * 业务约定：
 *   - 提供两种等价的登录模式（顶部 segmented control）：飞书 SSO / 账号密码。
 *   - 默认模式为飞书 SSO。
 *   - 账号密码模式下方提示"账号需要管理员预先绑定"，引导未绑定用户走飞书 SSO。
 */
const UserLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [mode, setMode] = useState<LoginMode>('feishu');
  const [submitting, setSubmitting] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const getReturnPath = () => {
    const params = new URL(window.location.href).searchParams;
    return params.get('origin') || '/';
  };

  const handleFeishuLogin = async () => {
    setSubmitting(true);
    try {
      const callbackUrl = `${window.location.origin}/callback/login`;
      sessionStorage.setItem(PASSGATE_RETURN_PATH_KEY, getReturnPath());
      const loginUrl = await getPassgateLoginUrl(callbackUrl);
      if (!loginUrl) {
        message.error('无法获取 Passgate 登录地址，请确认仿真后端配置已注入 neolix.passgate.public-url');
        return;
      }
      window.location.href = loginUrl;
    } catch (e) {
      console.error('飞书登录失败:', e);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const u = username.trim();
    const p = password;
    if (!u) {
      message.warning('请输入用户名或邮箱');
      return;
    }
    if (!p) {
      message.warning('请输入密码');
      return;
    }
    setSubmitting(true);
    try {
      const tokens = await loginByPassword(u, p);
      if (!tokens?.accessToken) {
        message.error('登录失败：未返回 access_token');
        return;
      }
      setLoginTokens('local', tokens.accessToken, tokens.refreshToken);
      resetCurrentUserCache();
      message.success(`欢迎回来，${tokens.user.name}`);
      navigate(getReturnPath(), { replace: true });
    } catch (err) {
      console.error('密码登录失败:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="user-login-page">
      {/* 左：蓝图视觉区 */}
      <section className="ul-stage" aria-hidden="true">
        <div className="ul-grid" />
        <div className="ul-noise" />

        <header className="ul-brand">
          <div className="ul-brand-mark">⌁</div>
          <span className="ul-brand-name">SIM</span>
          <span className="ul-brand-sep">·</span>
          <span>NEOLIX SIMULATION CONTROL</span>
        </header>

        <div className="ul-hero">
          <span className="ul-eyebrow">// build · evaluate · iterate</span>
          <h1 className="ul-headline">
            驾驶仿真<br />
            <em>从场景到结论</em>
          </h1>
          <p className="ul-subline">
            构建、回放、评测、对比 —— 用统一控制台串起 <code>scene</code>、
            <code>job</code>、<code>task</code> 与 <code>evaluator</code>，让每一次迭代都有据可循。
          </p>

          <div className="ul-canvas">
            <span className="ul-corner tl" />
            <span className="ul-corner tr" />
            <span className="ul-corner bl" />
            <span className="ul-corner br" />
            <span className="ul-canvas-label">trajectory / sim-run #001</span>
            <svg viewBox="0 0 640 260" preserveAspectRatio="none" role="img">
              <defs>
                <linearGradient id="ul-trace-grad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#6aa3ff" />
                  <stop offset="100%" stopColor="#4adfb5" />
                </linearGradient>
              </defs>

              {Array.from({ length: 11 }).map((_, i) => {
                const x = 20 + i * 60;
                return (
                  <g key={`t-${i}`}>
                    <line className="ul-tick" x1={x} y1={236} x2={x} y2={244} />
                    <text className="ul-tick-num" x={x - 8} y={256}>{(i * 100).toString().padStart(3, '0')}</text>
                  </g>
                );
              })}

              <path
                id="ul-trace-path"
                className="ul-trace"
                d="M 20 200 C 80 200, 110 170, 150 150 S 220 80, 280 90 S 380 170, 440 150 S 540 60, 620 70"
              />

              <circle cx="20" cy="200" r="3" className="ul-marker" />
              <circle cx="620" cy="70" r="3" className="ul-marker" />

              <circle r="4" fill="#fff" opacity="0.95">
                <animateMotion dur="8s" repeatCount="indefinite" begin="0.4s" rotate="auto">
                  <mpath href="#ul-trace-path" />
                </animateMotion>
              </circle>
            </svg>
          </div>
        </div>

        <footer className="ul-meta">
          <span><b>env</b>{window.location.host || 'localhost'}</span>
          <span><b>auth</b>passgate · feishu sso</span>
          <span><b>ts</b>{NOW_LABEL}Z</span>
        </footer>
      </section>

      {/* 右：登录卡 */}
      <aside className="ul-panel">
        <div className="ul-card">
          <div className="ul-card-eyebrow">secure access</div>
          <h2 className="ul-card-title">登录到仿真平台</h2>
          <p className="ul-card-sub">选择一种方式继续 —— 飞书 SSO 推荐，账号密码作为兜底。</p>

          {/* segmented control：两种模式等价 */}
          <div
            className="ul-segmented"
            data-mode={mode}
            role="tablist"
            aria-label="登录方式"
          >
            <span className="ul-segmented-indicator" aria-hidden="true" />
            <button
              type="button"
              className="ul-segment"
              role="tab"
              aria-selected={mode === 'feishu'}
              onClick={() => setMode('feishu')}
            >
              <span className="ul-segment-dot" />
              飞书登录
            </button>
            <button
              type="button"
              className="ul-segment"
              role="tab"
              aria-selected={mode === 'password'}
              onClick={() => setMode('password')}
            >
              <span className="ul-segment-dot" />
              账号密码
            </button>
          </div>

          {/* feishu tab */}
          {mode === 'feishu' && (
            <div className="ul-pane" role="tabpanel">
              <button
                type="button"
                className="ul-feishu-btn"
                onClick={handleFeishuLogin}
                disabled={submitting}
                autoFocus
              >
                <FeishuIcon className="ul-feishu-icon" />
                <span>{submitting ? '跳转中…' : '使用飞书账号登录'}</span>
                <svg className="ul-feishu-arrow" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                  <path
                    d="M3 8h9M8 4l4 4-4 4"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          )}

          {/* password tab */}
          {mode === 'password' && (
            <div className="ul-pane" role="tabpanel">
              <form className="ul-form" onSubmit={handlePasswordSubmit}>
                <div className="ul-field">
                  <label htmlFor="ul-username">username · email</label>
                  <input
                    id="ul-username"
                    className="ul-input"
                    placeholder="username 或 xxx@neolix.ai"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="username"
                    autoFocus
                  />
                </div>
                <div className="ul-field">
                  <label htmlFor="ul-password">password</label>
                  <input
                    id="ul-password"
                    className="ul-input"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                </div>
                <button type="submit" className="ul-submit" disabled={submitting}>
                  {submitting ? '验证中…' : '使用账号登录'}
                </button>
              </form>

              <div className="ul-tip" role="note">
                <InfoIcon className="ul-tip-icon" />
                <div>
                  <b>账号需由管理员预先绑定</b>后才可登录。
                  尚未绑定？请先切换到「飞书登录」用企业账号入站，随后由管理员授予角色。
                </div>
              </div>
            </div>
          )}

          <div className="ul-card-foot">
            same email · single user record<br />
            first feishu login defaults to <span style={{ color: '#6aa3ff' }}>viewer</span>; roles managed by admin.
          </div>
        </div>
      </aside>
    </div>
  );
};

export default UserLoginPage;
